import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { TranslateService } from '@ngx-translate/core';
import { AuthRequestDto, AuthResponseDto, Member, Role } from '@xfeet-workspace/xfeet-api-client/models';
import { AuthApi, MemberApi } from '@xfeet-workspace/xfeet-api-client/services';
import { BehaviorSubject } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private member?: Member;
  public isLoggedIn$ = new BehaviorSubject<boolean>(false);
  // Checking if token is set and not expired
  public get isLoggedIn() {
    return (this.jwtHelper.tokenGetter() !== null && !this.jwtHelper.isTokenExpired());
  }

  // Checking if token is set
  public get currentMember(): Member | undefined {
    const storedMember = localStorage.getItem('member');
    return this.isLoggedIn ? (this.member === undefined && storedMember ? Object.assign({}, JSON.parse(storedMember)) : this.member) : undefined;
  }

  public getLocale(): string {
    if (this.isLoggedIn) {
      return this.currentMember?.language?.code || navigator.language;
    }
    return navigator.language;
  }

  // Checking if membername is same
  public isMe(membername: string = ''): boolean {
    return this.currentMember === undefined ? false : this.currentMember?.email.toUpperCase() === membername.toUpperCase();
  }

  constructor(
    private router: Router,
    private authApi: AuthApi,
    private memberApi: MemberApi,
    private translate: TranslateService,
    private jwtHelper: JwtHelperService) {
    if (this.isLoggedIn) {
      this.isLoggedIn$.next(true);
      const storedMember = localStorage.getItem('member');
      this.member = storedMember ? Object.assign({}, JSON.parse(storedMember)) : undefined;
      this.translate.use(this.member?.language?.code || this.getLocale());
    } else {
      this.isLoggedIn$.next(false);
    }
  }

  public getToken() {
    return this.jwtHelper.tokenGetter();
  }

  public changeLanguage(languageId: number) {
    this.memberApi.updateOneBaseMemberControllerMember({
      id: this.currentMember!.id!,
      body: {
        language: {
          id: languageId,
        }
      }
    }).pipe(
      switchMap(() => this.memberApi.getOneBaseMemberControllerMember({id: this.currentMember!.id!}))
    )
    .subscribe({
      next: (me: Member) => {
        console.debug(me);
        localStorage.setItem('member', JSON.stringify(me));
        this.member = Object.assign({}, me);
        window.location.reload();
      }
    });
  }

  /*register(member: Member) {
    return this.httpClient
    .post<any>(environment.apiRootUrl + 'members/register',
      {
        lastName: member.lastName,
        firstName: member.firstName,
        email: member.email,
        password: member.password
      },
      this.httpOptions
    )
    .pipe(
      tap((loginResponse) => { this.save(loginResponse); })
    );
  }*/

  login(auth: AuthRequestDto) {
    return this.authApi.authControllerLogin({body: auth})
    .pipe(
      tap((loginResponse) => { this.save(loginResponse); })
    );
  }

  loginRedirect() {
    this.router.navigate(['/dashboard']);
  }

  // After clearing localStorage redirect to login screen
  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('member');
    this.isLoggedIn$.next(false);
    return this.router.navigate(['/auth/login']);
  }

  memberUpdated(member: Member) {
    localStorage.setItem('member', JSON.stringify(member));
    this.member = Object.assign({}, member);
    this.isLoggedIn$.next(true);
  }

  // persist to local storage
  private save(loginResponse: AuthResponseDto) {
    if (loginResponse !== null && loginResponse !== undefined) {
      if (loginResponse.access_token !== null && loginResponse.access_token !== undefined) {
        localStorage.setItem('token', loginResponse.access_token);
      }
      if (loginResponse.member !== null && loginResponse.member !== undefined) {
        localStorage.setItem('member', JSON.stringify(loginResponse.member));
        this.member = Object.assign({}, loginResponse.member);
        this.isLoggedIn$.next(true);
        this.translate.use(this.member?.language?.code || this.getLocale());
      }
    }
  }

  public hasOneOfRoles(roles?: string[]) {
    return (roles?.filter((r) => this.currentMember?.roles?.find((memberRole: Role) => memberRole?.code === r))?.length || 0) > 0;
  }

  // persist to local storage
  public saveMember(member: Member) {
    if (member !== null && member !== undefined) {
      localStorage.setItem('member', JSON.stringify(member));
      this.member = Object.assign({}, member);
    }
  }

}
